import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box } from '@mui/system'
import { Collapse, IconButton, Typography, useTheme } from '@mui/material'
import { ChevronRight } from '@mui/icons-material'
import { Caption, H5 } from '../../../../layouts/TextStyles'
import {
    HomeworkAnalysisState,
} from '../../../../features/types'
import HomeworkCardSection from './HomeworkCardSection'
import { capitalize, formatDateTime } from '../../../../utils/functions'
import Document from '../../../../assets/icons/Documents/Document.svg'
import CircleCheck from '../../../../assets/icons/CircleCheck.svg'
import dayjs from "dayjs";
import { tone, ui } from '../../../../utils/uiTokens'

interface Props {
    homework: HomeworkAnalysisState
    refetch?: () => void
}

const TopicHomeworkCard: React.FC<Props> = ({ homework, refetch }) => {
    const theme = useTheme()
    const [expanded, setExpanded] = useState(false)
    const { formatMessage, locale } = useIntl()
    const date = new Date();

    const getHomeworkColor = (homework) => {
        const now = new Date();
        const end = new Date(homework?.homework?.endDateTime);
        const start = new Date(homework?.homework?.startDateTime);
        // 1) base by status
        if (homework?.status === 'COMPLETED') {
            return {
                backgroundColor: tone.ok.bg,
                color: tone.ok.text,
                text: formatMessage({ id: 'COMPLETED' }),
            }
        }
        if (homework?.status === 'SUBMITTED') {
            return {
                backgroundColor: tone.warn.bg,
                color: tone.warn.text,
                text: formatMessage({ id: 'SUBMITTED' }),
            }
        }
        if (homework?.status === 'NOT_STARTED') {
            if (end < now) return {
                backgroundColor: tone.bad.bg,
                color: tone.bad.text,
                text: formatMessage({ id: 'skipped' }),
            }
            if (start > now) return {
                backgroundColor: tone.info.bg,
                color: tone.info.text,
                text: formatMessage({ id: 'NOT_STARTED' }),
            }
            return {
                backgroundColor: ui.track,
                color: ui.ink,
                text: formatMessage({ id: 'NOT_SENT' }),
            }
        }
        return {
            backgroundColor: ui.track,
            color: ui.ink,
            text: formatMessage({ id: 'NOT_SENT' }),
        }
    };

    const styles = {
        wrapper: {
            border: `1px solid ${theme.palette.grey[100]}`,
            borderRadius: '8px',
            mb: 2,
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: '8px',
            backgroundColor: getHomeworkColor(homework),
            cursor: 'pointer',
            p: 1,
            pr: 2,
        },
        chevron: {
            transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: '0.15s ease-in-out',
        },
        flexCenter: {
            display: 'flex',
            alignItems: 'center',
        },
        flexBetween: {
            display: 'flex',
            justifyContent: 'space-between',
            '@media (max-width: 768px)': {
                flexDirection: 'column',
                gap: 2,
                alignItems: 'flex-start',
            },
        },
        topicProgress: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            marginLeft: 1,
        },
        info: {
            gap: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            '@media (max-width: 768px)': {
                gap: 6,
            },
        },
    }




    return (
        <Box sx={styles.wrapper}>
            <Box
                sx={styles.container}
                onClick={() => {
                    setExpanded(!expanded)
                }}
            >
                <Box sx={{ ...styles.flexCenter, width: '95%' }}>
                    <IconButton>
                        <ChevronRight sx={styles.chevron} />
                    </IconButton>
                    <Box sx={styles.topicProgress}>
                        <Box sx={styles.flexBetween}>
                            <Box>
                                <H5>{capitalize(homework?.homework?.title)}</H5>
                                <Caption style={{ fontWeight: 300 }}>{"(" + homework[`topic${capitalize(locale)}`] + ")"}</Caption>
                            </Box>
                            <Caption style={{ fontWeight: 300, alignSelf: 'center' }}> {formatMessage({ id: 'homework_duration' })} :<br /> {dayjs(homework?.homework?.startDateTime).format('DD.MM.YYYY HH:mm') + ' -- ' + dayjs(homework?.homework?.endDateTime).format('DD.MM.YYYY HH:mm')}</Caption>
                            <Box sx={styles.info}>
                                <H5 color={getHomeworkColor(homework)?.color}>{getHomeworkColor(homework)?.text}</H5>
                                <H5 >{homework?.status === 'COMPLETED' && homework?.point + ' ' + formatMessage({ id: 'points' })} </H5>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {homework?.status === 'SUBMITTED' && <img src={Document} alt={homework?.homework?.title} />}
                {homework?.status === 'COMPLETED' && <img src={CircleCheck} alt={homework?.homework?.title} />}
            </Box>

            <Collapse in={expanded}>
                <Box>
                    <HomeworkCardSection
                        homework={homework}
                        refetch={refetch}
                    />
                </Box>
            </Collapse>
        </Box >
    )
}

export default TopicHomeworkCard
